// src/Pages/DashboardAssistant/DashboardTileDragable.jsx

import React, { useState } from 'react';
import { ResizableBox } from "react-resizable";
import { useDraggable } from "@dnd-kit/core";
import DynamicChart from '../../Components/DynaicChart/DynamicChart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faInfo, faGripVertical, faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { Button, OverlayTrigger, Popover, Dropdown } from 'react-bootstrap';

const DashboardTileDragable = ({ tile, removeTile, onResize, gridRef, snapSize = 30, readOnly = false, onUpdateTile }) => {
  const { id, title, description, chartSpec, data, position } = tile;
  const x = position?.x ?? 0;
  const y = position?.y ?? 0;
  const width = Math.max(position?.width ?? 320, snapSize);
  const height = Math.max(position?.height ?? 240, snapSize);
  const [liveSize, setLiveSize] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(title || chartSpec?.title || 'Chart');

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    disabled: readOnly,
  });

  const translateX = transform?.x ?? 0;
  const translateY = transform?.y ?? 0;

  const applyResize = (newW, newH, snapToGrid) => {
    if (typeof onResize !== "function") return;
    const widthVal = snapToGrid
      ? Math.max(snapSize, Math.round(newW / snapSize) * snapSize)
      : Math.max(snapSize, newW);
    const heightVal = snapToGrid
      ? Math.max(snapSize, Math.round(newH / snapSize) * snapSize)
      : Math.max(snapSize, newH);

    if (readOnly) return;
    onResize(id, {
      width: widthVal,
      height: heightVal,
      x,
      y,
    });
  };

  const style = {
    position: "absolute",
    left: x,
    top: y,
    width: liveSize?.width ?? width,
    height: liveSize?.height ?? height,
    transform: readOnly ? undefined : `translate3d(${translateX}px, ${translateY}px, 0)`,
    zIndex: isDragging ? 10 : 1,
  };

  const commitTitle = () => {
    setEditingTitle(false);
    onUpdateTile?.(id, { title: titleDraft });
  };

  const isFrameVisible = tile.showFrame !== false;
  const content = (
    <div className={`da-tile card h-100${isFrameVisible ? "" : " da-tile--transparent"}`}>
      <div className="da-tile-header">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center flex-wrap gap-2">
            {!readOnly && (
              <Button
                variant="light"
                size="xs"
                className="tile-drag-handle"
                type="button"
                {...listeners}
                title="Drag tile"
              >
                <FontAwesomeIcon icon={faGripVertical} />
              </Button>
            )}
            <OverlayTrigger
              trigger="click"
              placement="right-end"
              overlay={
                <Popover>
                  <Popover.Body>
                    {description && <small>{description}</small>}
                  </Popover.Body>
                </Popover>
              }
            >
              <Button variant="outline-secondary" size='xs'>
                <FontAwesomeIcon icon={faInfo} />
              </Button>
            </OverlayTrigger>
          </div>
          {!readOnly && (
            <div className="d-flex align-items-center gap-2">
              <Dropdown align="end">
                <Dropdown.Toggle variant="outline-secondary" size="xs">
                  <FontAwesomeIcon icon={faEllipsisVertical} />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => onUpdateTile?.(id, { showTitle: !(tile.showTitle !== false) })}>
                    {tile.showTitle === false ? 'Show title' : 'Hide title'}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => onUpdateTile?.(id, { showLegend: !(tile.showLegend ?? true) })}>
                    {tile.showLegend === false ? 'Show legend' : 'Hide legend'}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => onUpdateTile?.(id, { showAxis: !(tile.showAxis ?? true) })}>
                    {tile.showAxis === false ? 'Show axes' : 'Hide axes'}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => onUpdateTile?.(id, { showGrid: !(tile.showGrid ?? true) })}>
                    {tile.showGrid === false ? 'Show grid' : 'Hide grid'}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => onUpdateTile?.(id, { showFrame: !(tile.showFrame !== false) })}>
                    {tile.showFrame === false ? 'Show border/background' : 'Hide border/background'}
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  {(tile.tileType === 'text' || tile.tileType === 'image') && (
                    <Dropdown.Item onClick={() => onUpdateTile?.(id, { __editContent: true })}>
                      Edit content
                    </Dropdown.Item>
                  )}
                  <Dropdown.Item onClick={() => setEditingTitle(true)}>Edit title</Dropdown.Item>
                  <Dropdown.Item disabled>Edit prompt (coming soon)</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              <button className="btn btn-xs btn-outline-danger" onClick={() => removeTile(id)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          )}
        </div>
        {(tile.showTitle ?? true) && (
          <div className="mt-0 fw-semibold text-center">
            {editingTitle && !readOnly ? (
              <input
                className="form-control form-control-sm"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitTitle();
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    setEditingTitle(false);
                    setTitleDraft(title || chartSpec?.title || 'Chart');
                  }
                }}
                autoFocus
              />
            ) : (
              <div className="text-truncate" title={title || chartSpec?.title || 'Chart'}>
                {title || chartSpec?.title || 'Chart'}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="da-tile-body">
        {isResizing && !readOnly ? (
          <div className="text-muted small">Resizing…</div>
        ) : tile.tileType === 'table' ? (
          <div className="table-responsive" style={{ maxHeight: "100%", overflow: "auto" }}>
            <table className="table table-sm table-striped mb-0">
              <thead>
                <tr>
                  {(tile.columns && tile.columns.length ? tile.columns : Object.keys(data?.[0] ?? {})).map((col) => (
                    <th key={col}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data ?? []).map((row, idx) => (
                  <tr key={idx}>
                    {(tile.columns && tile.columns.length ? tile.columns : Object.keys(row)).map((col) => (
                      <td key={col}>{row?.[col]}</td>
                    ))}
                  </tr>
                ))}
                {(!data || !data.length) && (
                  <tr><td colSpan={tile.columns?.length || 1} className="text-muted">No data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        ) : tile.tileType === 'text' ? (
          <div className="text-wrap overflow-y-auto h-100">
            <div
              className="text-secondary"
              dangerouslySetInnerHTML={{ __html: tile.textContent || description || 'No insight available.' }}
            />
          </div>
        ) : tile.tileType === 'image' ? (
          tile.imageUrl ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <img
                src={tile.imageUrl}
                alt={title || 'Tile image'}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            </div>
          ) : (
            <div className="text-muted small">No image provided.</div>
          )
        ) : (
          <DynamicChart
            spec={chartSpec}
            data={data}
            showLegend={tile.showLegend ?? true}
            showAxis={tile.showAxis ?? true}
            showGrid={tile.showGrid ?? true}
          />
        )}
      </div>
    </div>
  );

  if (readOnly) {
    return (
      <div ref={setNodeRef} style={style} className="da-tile-wrapper">
        <div style={{ width: width, height: height }}>
          {content}
        </div>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style} className="da-tile-wrapper" {...attributes}>
      <ResizableBox
        width={liveSize?.width ?? width}
        height={liveSize?.height ?? height}
        onResize={(e, data) => {
          setIsResizing(true);
          setLiveSize({ width: data.size.width, height: data.size.height });
        }}
        onResizeStop={(e, data) => {
          setLiveSize(null);
          setIsResizing(false);
          applyResize(data.size.width, data.size.height, true);
        }}
        minConstraints={[snapSize, snapSize]}
        maxConstraints={[Infinity, Infinity]}
        resizeHandles={["se"]}
      >
        {content}
      </ResizableBox>
    </div>
  );
};

export default DashboardTileDragable;
